import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { HeartPulseIcon, ShieldIcon, AlertTriangleIcon, UsersIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
interface ServiceCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  benefits: string[];
  delay: number;
}
function ServiceCard({
  icon,
  title,
  description,
  benefits,
  delay
}: ServiceCardProps) {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 30
  }} animate={inView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.6,
    delay
  }} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-2xl transition-shadow" data-id="element-184">
      <div className="flex justify-center mb-6" data-id="element-185">
        <div className="w-16 h-16 bg-[#d97706] rounded-full flex items-center justify-center text-white" data-id="element-186">
          {icon}
        </div>
      </div>
      <h3 className="text-2xl font-bold text-[#1a365d] mb-4 text-center" data-id="element-187">
        {title}
      </h3>
      <p className="text-gray-700 mb-6 leading-relaxed" data-id="element-188">{description}</p>
      <div className="space-y-3" data-id="element-189">
        {benefits.map((benefit, index) => <div key={index} className="flex items-start gap-3" data-id="element-190">
            <div className="w-2 h-2 bg-[#d97706] rounded-full mt-2 flex-shrink-0" data-id="element-191"></div>
            <p className="text-gray-700" data-id="element-192">{benefit}</p>
          </div>)}
      </div>
    </motion.div>;
}
export function ServicesSection() {
  const {
    t
  } = useLanguage();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const services = [{
    icon: <HeartPulseIcon className="w-8 h-8" data-id="element-193" />,
    title: t('Planes de Salud', 'Health Plans'),
    description: t('Te ayudo a encontrar el plan de salud ideal para ti y tu familia. Exploro contigo opciones como planes HMO, PPO, planes de alto deducible y coberturas complementarias, siempre adaptadas a tu presupuesto y necesidades.', 'I help you find the ideal health plan for you and your family. I explore options with you such as HMO, PPO, high-deductible plans, and supplemental coverage, always tailored to your budget and needs.'),
    benefits: [t('Acceso a una red de proveedores', 'Access to a provider network'), t('Cobertura de medicamentos', 'Prescription drug coverage'), t('Programas preventivos', 'Preventive programs')]
  }, {
    icon: <ShieldIcon className="w-8 h-8" data-id="element-194" />,
    title: t('Seguros de Vida', 'Life Insurance'),
    description: t('Protege el futuro de tus seres queridos con un seguro de vida a tu medida. Exploro contigo pólizas de término, vida entera o universal, y te asesoro para que elijas la protección que garantice tranquilidad financiera.', 'Protect the future of your loved ones with a tailored life insurance policy. I explore term, whole life, or universal policies with you, and advise you so you choose the protection that guarantees financial peace of mind.'),
    benefits: [t('Respaldo económico', 'Financial backing'), t('Creación de patrimonio', 'Wealth creation'), t('Seguridad para tu familia', 'Security for your family')]
  }, {
    icon: <AlertTriangleIcon className="w-8 h-8" data-id="element-195" />,
    title: t('Seguros de Accidentes', 'Accident Insurance'),
    description: t('Un accidente puede ocurrir en cualquier momento. Con este seguro, te ofrezco una red de protección financiera para cubrir gastos médicos, pérdida de ingresos y tratamientos de rehabilitación.', 'An accident can happen at any time. With this insurance, I offer you a financial safety net to cover medical expenses, loss of income, and rehabilitation treatments.'),
    benefits: [t('Indemnizaciones rápidas', 'Fast payouts'), t('Cobertura de hospitalización', 'Hospitalization coverage'), t('Apoyo en momentos críticos', 'Support in critical moments')]
  }, {
    icon: <UsersIcon className="w-8 h-8" data-id="element-196" />,
    title: 'Medicare',
    description: t('Acompaño a los adultos mayores en el camino de elegir su plan de Medicare. Te ayudo a entender las partes A, B, C y D, y a combinarlas con planes suplementarios o Advantage, para garantizar una cobertura completa y sin sorpresas.', 'I accompany seniors on the journey of choosing their Medicare plan. I help you understand parts A, B, C, and D, and combine them with supplemental or Advantage plans to guarantee complete coverage without surprises.'),
    benefits: [t('Asesoría personalizada', 'Personalized advice'), t('Planes suplementarios', 'Supplemental plans'), t('Decisiones informadas', 'Informed decisions')]
  }];
  return <section id="servicios" className="py-20 bg-gray-50" data-id="element-197">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-198">
        <motion.div ref={ref} initial={{
        opacity: 0,
        y: 30
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-199">
          <h2 className="text-4xl md:text-5xl font-bold text-[#1a365d] mb-4" data-id="element-200">
            {t('Mis Servicios', 'My Services')}
          </h2>
          <div className="w-24 h-1 bg-[#d97706] mx-auto mb-6" data-id="element-201"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-202">
            {t('Protección integral para ti y tu familia', 'Comprehensive protection for you and your family')}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8" data-id="element-203">
          {services.map((service, index) => <ServiceCard key={index} {...service} delay={0.2 + index * 0.1} data-id="element-204" />)}
        </div>
      </div>
    </section>;
}